import type { ReactNode } from 'react'
import { Link as RouterLink, useParams, useSearchParams } from 'react-router-dom'
import { Box, Chip, Link, Stack, Typography } from '@mui/material'
import { ArrowLeftIcon, ArrowUpRightIcon } from 'lucide-react'

import { Alert, Card, Skeleton, Tabs, TABS_CONTENT_GAP } from '@repo/ui'
import { Header, StatusChip } from 'components'
import { ARTICLE_STATES } from 'constants/statuses'
import { useArticleApprovals, useArticlePublications } from 'hooks/api'
import { useArticleDetail, useCoverImageUrl, useCoverState } from 'hooks/api/useArticleCovers'
import { ARTICLE_TABS, type ArticleTab } from 'routes'
import { ROUTES } from 'routes/routes'
import { articleContentApi, type ArticleDetail, type ContentBlock, type WebsiteArticle } from 'services/coversApi'

import { formatIsoDay } from '../GtmTradeShowsPage/tradeShowModel'
import { ArticleCoverPanel } from './ArticleCoverPage'
import { ArticleReviewSteps } from './ArticleReviewSteps'
import { CoverPlacements } from './CoverPlacements'
import { approvalKey, articleLink, articleState, coverLabel } from './articleModel'

/**
 * `/internal/gtm/articles/:section/:slug` — VRG-733 (Spencer, 2026-10-22: "clicking the row in
 * /articles should go to an article-specific page where I see the content, the cover generator,
 * etc."). Tabs via `?tab=` (`ARTICLE_TABS`):
 *
 *   Overview (default)  the article as the website has it, read-only and read FRESH from the
 *                       website's base branch (`/internal/gtm/article-content`): title, lead and
 *                       every body block, beside its metadata (type, format, solutions,
 *                       industries, dates, length, cover, card summary, publish state, preview /
 *                       live links, sources). Not pixel parity with vera.inc: the words, in order.
 *   Cover               the cover tool (`ArticleCoverPanel`). Its first home, `…/:slug/cover`,
 *                       redirects here (`legacyArticleCoverTarget`).
 *
 * Above the tabs, the REVIEW STEPS (`ArticleReviewSteps`, 2026-10-29): Words · Cover · Approve ·
 * Narration · Preview · Publish, each with its action here. The Overview opens with the cover in
 * the three frames readers meet it in (`CoverPlacements`) when the piece has one.
 *
 * TABS THAT COME LATER, deliberately not added empty (an empty tab is a promise the page cannot
 * keep):
 *   - Audio: article narration is being built on the website branch `agent/article-audio`;
 *     its tab lands with that work.
 *   - Analytics: follows the content-analytics spec in vera-inc
 *     `product/vera2-verafied-series-brief.md` §4.
 */
const TAB_LABELS: Record<ArticleTab, string> = { overview: 'Overview', cover: 'Cover' }

export const ArticleDetailPage = () => {
  const { section = 'insights', slug = '' } = useParams()
  const [params, setParams] = useSearchParams()
  const requested = params.get('tab')
  const tab: ArticleTab = (ARTICLE_TABS as readonly string[]).includes(requested ?? '') ? (requested as ArticleTab) : 'overview'
  const setTab = (value: string) => {
    const next = new URLSearchParams(params)
    if (value === 'overview') next.delete('tab')
    else next.set('tab', value)
    setParams(next, { replace: true })
  }
  const { data, isLoading, error } = useArticleDetail(section, slug)
  const a = data?.article ?? null
  const title = a?.title ?? data?.content?.title ?? slug
  const tabs = ARTICLE_TABS.map((value) => ({ value, label: TAB_LABELS[value] }))

  return (
    <>
      <Link component={RouterLink} to={ROUTES.INTERNAL_GTM_ARTICLES} underline="hover" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <ArrowLeftIcon size={14} strokeWidth={1.5} />
        <Typography variant="body3">Articles</Typography>
      </Link>
      <Header title={isLoading ? 'Loading…' : title} description={a ? `${a.href ?? a.path} · ${section === 'learn' ? 'Explainer' : (a.type ?? a.kind)}` : undefined} />
      {a ? <ArticleReviewSteps article={a} onTab={setTab} /> : null}
      <Tabs redirect={false} tabs={tabs} value={tab} onChange={setTab} />
      <Box sx={{ mt: `${TABS_CONTENT_GAP}px` }}>
        {tab === 'cover' ? (
          <ArticleCoverPanel section={section} slug={slug} title={title} />
        ) : error ? (
          <Alert severity="error" title="Could not load the article" description={(error as Error).message} />
        ) : isLoading || !data ? (
          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={4}>
            <Skeleton height={480} sx={{ flex: 2 }} />
            <Skeleton height={480} sx={{ flex: 1 }} />
          </Stack>
        ) : (
          <Overview section={section} slug={slug} d={data} />
        )}
      </Box>
    </>
  )
}

const Overview = ({ section, slug, d }: { section: string; slug: string; d: ArticleDetail }) => {
  const c = d.content
  return (
    <Stack direction={{ xs: 'column', lg: 'row' }} spacing={4} alignItems="flex-start">
      <Stack spacing={4} sx={{ flex: 2, minWidth: 0, width: '100%' }}>
      {d.article ? <CoverPlacements article={d.article} /> : null}
      <Card sx={{ p: 5, mb: 0, width: '100%' }}>
        <Stack spacing={3} sx={{ maxWidth: 760 }}>
          {d.manifestReason ? <Alert severity="warning" title="The website manifest could not be read" description={d.manifestReason} /> : null}
          <Typography variant="h4">{c?.title ?? d.article?.title}</Typography>
          {c?.lead ? (
            <Typography variant="body1" color="text.secondary">
              {c.lead}
            </Typography>
          ) : null}
          {c?.body ? (
            c.body.map((b, i) => <Block key={i} b={b} />)
          ) : (
            <Alert severity="info" title="The body is not in a content file" description={c?.bodyNote ?? d.contentNote ?? ''} />
          )}
        </Stack>
      </Card>
      </Stack>
      <Box sx={{ flex: 1, minWidth: 300, width: '100%' }}>
        <Meta section={section} slug={slug} d={d} />
      </Box>
    </Stack>
  )
}

/**
 * An explainer's figure (2026-10-29): the website DRAWS it (ArticleFigures.tsx); here are its label,
 * title, data, caption and alt, so the review covers what the drawing says. The drawing itself is
 * on the staged page.
 */
const Figure = ({ b, cites }: { b: ContentBlock; cites: ReactNode }) => (
  <Box sx={(t) => ({ border: `1px solid ${t.palette.primary[100]}`, borderRadius: '8px', p: 3 })}>
    <Typography variant="body4" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block' }}>
      Figure · {b.eyebrow}
    </Typography>
    <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
      {b.text}
      {cites}
    </Typography>
    <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
      <tbody>
        {(b.pairs ?? []).map((p, i) => (
          <Box component="tr" key={i} sx={(t) => ({ borderTop: i ? `1px solid ${t.palette.primary[100]}` : undefined })}>
            <Box component="td" sx={{ py: 1, pr: 3, verticalAlign: 'top', width: '38%' }}>
              <Typography variant="body3" sx={{ fontWeight: 500, whiteSpace: 'pre-wrap' }}>
                {p.label}
              </Typography>
            </Box>
            <Box component="td" sx={{ py: 1, verticalAlign: 'top' }}>
              <Typography variant="body3" color="text.secondary">
                {p.value}
              </Typography>
            </Box>
          </Box>
        ))}
      </tbody>
    </Box>
    {b.caption ? (
      <Typography variant="body4" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
        {b.caption}
      </Typography>
    ) : null}
    {b.alt ? (
      <Typography variant="body4" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        Alt text: {b.alt}
      </Typography>
    ) : null}
    <Typography variant="body4" color="text.secondary" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
      The drawing is on the staged page; this is what it shows.
    </Typography>
  </Box>
)

const Block = ({ b }: { b: ContentBlock }) => {
  const cites = b.cites?.length ? (
    <Typography component="sup" variant="body4" color="text.secondary" sx={{ ml: 0.5 }}>
      [{b.cites.join(', ')}]
    </Typography>
  ) : null
  switch (b.kind) {
    case 'heading':
      return (
        <Box sx={{ pt: 2 }}>
          {b.eyebrow ? (
            <Typography variant="body4" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block' }}>
              {b.eyebrow}
            </Typography>
          ) : null}
          <Typography variant="h6">{b.text}</Typography>
        </Box>
      )
    case 'paragraph':
      return (
        <Typography variant="body2">
          {b.text}
          {cites}
        </Typography>
      )
    case 'list':
      return (
        <Box component="ul" sx={{ m: 0, pl: 4 }}>
          {(b.items ?? []).map((t, i) => (
            <Typography component="li" variant="body2" key={i} sx={{ mb: 1 }}>
              {t}
            </Typography>
          ))}
        </Box>
      )
    case 'figure':
      return <Figure b={b} cites={cites} />
    case 'other':
      return (
        <Typography variant="body3" color="text.secondary">
          {b.text}
        </Typography>
      )
    default:
      return (
        <Stack spacing={2} sx={{ borderLeft: '2px solid', borderColor: 'divider', pl: 3 }}>
          {(b.pairs ?? []).map((p, i) => (
            <Box key={i}>
              <Typography variant="body3" sx={{ fontWeight: 500 }}>
                {p.label}
                {p.status ? <Chip size="small" label={p.status} sx={{ ml: 1, height: 18 }} /> : null}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {p.value}
              </Typography>
            </Box>
          ))}
          {cites}
        </Stack>
      )
  }
}

const Row = ({ k, children }: { k: string; children: React.ReactNode }) => (
  <Stack direction="row" spacing={3} sx={{ py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
    <Typography variant="body3" color="text.secondary" sx={{ width: 120, flexShrink: 0 }}>
      {k}
    </Typography>
    <Box sx={{ minWidth: 0, flex: 1 }}>{typeof children === 'string' ? <Typography variant="body3">{children}</Typography> : children}</Box>
  </Stack>
)

/** `null` = not readable here (the website builds the entry), which is not the same as none. */
const chips = (xs: string[] | null | undefined) =>
  xs == null ? (
    '—'
  ) : xs.length ? (
    <Stack direction="row" flexWrap="wrap" gap={1}>
      {xs.map((x) => (
        <Chip key={x} size="small" label={x} />
      ))}
    </Stack>
  ) : (
    'None'
  )

const Meta = ({ section, slug, d }: { section: string; slug: string; d: ArticleDetail }) => {
  const a = d.article
  const c = d.content
  const { data: approvals } = useArticleApprovals()
  const { data: publications } = useArticlePublications()
  const approval = a ? (approvals ?? []).find((x) => approvalKey(x) === approvalKey(a)) : undefined
  const publication = a ? (publications ?? []).find((x) => approvalKey(x) === approvalKey(a)) : undefined
  const state = a ? articleState(a, approval, publication) : null
  const link = a ? articleLink({ path: a.path, href: a.href, status: state === 'published' ? 'published' : 'staged' }) : null

  return (
    <Card title="Details" sx={{ p: 4, mb: 0 }}>
      <Stack>
        <Row k="Status">{state ? <StatusChip statuses={ARTICLE_STATES} status={state} size="small" /> : 'Not in the website manifest'}</Row>
        <Row k="Links">
          {link ? (
            <Stack direction="row" spacing={3}>
              <ExtLink href={link.href} label={link.label === 'Live' ? 'Live' : 'Staged preview'} />
              {link.label === 'Live' ? null : (
                <Typography variant="body4" color="text.secondary">
                  opens only with the staging unlock
                </Typography>
              )}
            </Stack>
          ) : (
            '—'
          )}
        </Row>
        <Row k="Type">{section === 'learn' ? 'Explainer' : (c?.type ?? a?.type ?? a?.kind ?? '—')}</Row>
        <Row k="Format">{c?.format ?? a?.format ?? '—'}</Row>
        <Row k="Solutions">{chips(c?.solutions)}</Row>
        <Row k="Industries">{chips(c?.industries)}</Row>
        <Row k="Published">{a ? formatIsoDay(a.publishedAt) : '—'}</Row>
        <Row k="Updated">{a?.updatedAt ? formatIsoDay(a.updatedAt) : '—'}</Row>
        {a?.reviewedAt ? <Row k="Reviewed">{formatIsoDay(a.reviewedAt)}</Row> : null}
        <Row k="Length">{a ? `${a.wordCount.toLocaleString('en-US')} words${a.readMinutes ? ` · ${a.readMinutes} min read` : ''}` : '—'}</Row>
        <Row k="Sources">
          {a ? `${a.sourceCount} source${a.sourceCount === 1 ? '' : 's'}${c?.citedSources != null ? ` · ${c.citedSources} cited in the body` : ''}` : '—'}
        </Row>
        <Row k="Cover">{a ? <CoverMeta section={section} slug={slug} a={a} file={d.coverFile} /> : '—'}</Row>
        <Row k="Card summary">{c?.cardSummary ?? (c ? 'None (the card uses the excerpt)' : '—')}</Row>
        <Typography variant="body4" color="text.secondary" sx={{ pt: 2 }}>
          Read from {d.source ? d.source.label : 'the website'}
          {c ? ` · content from ${c.file}` : ''}
        </Typography>
      </Stack>
    </Card>
  )
}

const ExtLink = ({ href, label }: { href: string; label: string }) => (
  <Link href={href} target="_blank" rel="noreferrer" underline="hover" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
    <Typography variant="body3">{label}</Typography>
    <ArrowUpRightIcon size={14} strokeWidth={1} />
  </Link>
)

const CoverMeta = ({ section, slug, a, file }: { section: string; slug: string; a: WebsiteArticle; file: string | null }) => {
  const { src } = useCoverImageUrl(a.cover.type === 'image' ? articleContentApi.coverUrl(section, slug, 640) : null)
  const { data: cover } = useCoverState(section, slug, section === 'insights')
  const vera2 = section === 'insights' ? cover?.approval : null
  return (
    <Stack spacing={1.5}>
      {a.cover.type === 'image' ? (
        <Box sx={{ width: '100%', aspectRatio: '2400 / 1340', borderRadius: '6px', overflow: 'hidden', backgroundColor: '#0e0d12' }}>
          {src ? <img src={src} alt={a.cover.alt} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} /> : <Skeleton height="100%" />}
        </Box>
      ) : null}
      <Typography variant="body3">
        {coverLabel(a.cover, section)}
        {a.cover.type === 'image' ? ' on the website' : ''}
      </Typography>
      {file ? (
        <Typography variant="body4" color="text.secondary">
          {file}
        </Typography>
      ) : null}
      {vera2 ? (
        <Typography variant="body4" color="text.secondary">
          vera-2: approved {formatIsoDay(vera2.approvedAt.slice(0, 10))}
          {vera2.mode === 'mock' ? ' (mock)' : ''} · PR {vera2.pr?.status === 'opened' ? `#${vera2.pr.number} open` : (vera2.pr?.status ?? 'not opened')}
        </Typography>
      ) : null}
    </Stack>
  )
}
